import { useCallback } from 'react';
import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';
import type { SensorAlertBundle } from '@mark43/cad-api';
import { isSensorPanelMapAction } from './sensor-panel-map-action-types';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../action-broadcaster/use-integrated-action-broadcaster';
import { useApplicationType } from '../common/hooks/use-application-type';
import { isValidLatLng } from '../location/helpers';
import { useEsriMapStore } from '../map-window/hooks/use-esri-map-store';
import { useOpenSensorPanel } from './use-open-sensor-panel';
import { useOpenSensorEventPanel } from './use-open-sensor-event-panel';
import { useSensorsEnabled } from './use-sensors-enabled';
import { useSensorsStore } from './use-sensors-store';

/**
 * Opens sensor panels in the map window from any window, bringing the map window up first — the
 * same `OPEN_MAP_V2_WINDOW` pre-action "View on Map" uses.
 */
export function useOpenSensorPanelsOnMap(): {
    openSensorEventPanelOnMap: (alert: SensorAlertBundle) => void;
    openSensorPanelOnMap: (sensorId: string, options?: { centerMap?: boolean }) => void;
} {
    const { trigger } = useIntegratedActionBroadcaster();

    const openSensorEventPanelOnMap = useCallback(
        function openSensorEventPanelOnMap(alert: SensorAlertBundle) {
            trigger([
                { id: v4(), targetWindowType: 'ROOT', type: 'OPEN_MAP_V2_WINDOW' },
                {
                    id: v4(),
                    targetWindowType: 'MAP_V2',
                    type: 'OPEN_SENSOR_EVENT_PANEL',
                    /*
                     * A plain copy. In Electron the batch reaches the map window over IPC, which
                     * structured-clones it, and an alert read from a MobX store is a proxy that
                     * cannot be cloned: the send fails and the whole batch is dropped, the step
                     * that brings up the map window too, leaving only an unhandled rejection in
                     * the console. Not `toJS`, which stops at a plain object — and the event's
                     * hydrated alerts are plain copies spread from observable ones.
                     */
                    payload: { alert: cloneDeep(alert) },
                },
            ]);
        },
        [trigger]
    );

    const openSensorPanelOnMap = useCallback(
        function openSensorPanelOnMap(sensorId: string, { centerMap = false } = {}) {
            trigger([
                { id: v4(), targetWindowType: 'ROOT', type: 'OPEN_MAP_V2_WINDOW' },
                {
                    id: v4(),
                    targetWindowType: 'MAP_V2',
                    type: 'OPEN_SENSOR_PANEL',
                    payload: { sensorId, centerMap },
                },
            ]);
        },
        [trigger]
    );

    return { openSensorEventPanelOnMap, openSensorPanelOnMap };
}

/**
 * The map window's side: opens the requested panel in its own workspace. Renders nothing; mounted
 * in every window, but only the map window receives `MAP_V2` actions.
 */
export function SensorPanelMapActions(): null {
    const { isWeb } = useApplicationType();
    const sensorsEnabled = useSensorsEnabled();
    const sensorsStore = useSensorsStore();
    const openSensorEventPanel = useOpenSensorEventPanel();
    const openSensorPanel = useOpenSensorPanel();
    const mapStore = useEsriMapStore();

    const handleSensorPanelAction: ActionHandler<'MAP_V2'> = useCallback(
        function handleSensorPanelAction(action) {
            if (!isSensorPanelMapAction(action)) {
                return false;
            }
            switch (action.type) {
                case 'OPEN_SENSOR_EVENT_PANEL': {
                    const { alert } = action.payload;
                    /*
                     * Only when absent: the copy sent across may be a summary hydrated from the
                     * event, without `details`, and the map window's own copy — when it has one
                     * — is the fuller and fresher of the two.
                     */
                    if (!sensorsStore.isAlertHeld(alert)) {
                        sensorsStore.upsertAlerts([alert], { isLive: false });
                    }
                    openSensorEventPanel({ alertKey: alert.alertKey });
                    return true;
                }
                case 'OPEN_SENSOR_PANEL': {
                    const { sensorId, centerMap } = action.payload;
                    const sensor = sensorsStore.getSensor(sensorId);
                    // Centre only; the zoom is the dispatcher's.
                    if (centerMap && isValidLatLng(sensor?.latitude, sensor?.longitude)) {
                        mapStore.setMapCenterForUpdate({
                            latitude: sensor?.latitude as number,
                            longitude: sensor?.longitude as number,
                        });
                    }
                    openSensorPanel({ sensorId });
                    return true;
                }
                default:
                    return false;
            }
        },
        [sensorsStore, openSensorEventPanel, openSensorPanel, mapStore]
    );

    useIntegratedActionBroadcaster({
        id: 'SENSOR_PANELS',
        windowType: 'MAP_V2',
        // Mirrors the map's own view-on-map handler, which needs this on web to be reached at all.
        ignoreActiveWorkspaceWindowType: isWeb,
        // No handler, so nothing is registered, when sensors are off.
        handler: sensorsEnabled ? handleSensorPanelAction : undefined,
    });

    return null;
}
